import { useEffect, useMemo, useState } from "react";
import { Mail, Save, Send, RotateCcw, AlertCircle, CheckCircle2, XCircle, Eye, Code } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Lbl = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>
);

const TYPE_LABEL = {
  order_confirmation: "Confirmation client",
  new_order_admin: "Notification admin",
  password_reset: "Reset mot de passe",
  test: "Test",
};

function Tabs({ tabs, active, onChange }) {
  return (
    <div className="border-b border-stone mb-6 flex flex-wrap gap-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          data-testid={`comms-tab-${t.id}`}
          className={`px-4 py-3 text-xs uppercase tracking-[0.15em] -mb-px border-b-2 transition-colors ${
            active === t.id
              ? "border-copper text-ink"
              : "border-transparent text-ash hover:text-ink"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Settings tab ---------------- */
function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [admins, setAdmins] = useState("");
  const [enabled, setEnabled] = useState({
    order_confirmation: true,
    new_order_admin: true,
    password_reset: true,
  });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/superadmin/comms/settings").then(({ data }) => {
      setCfg(data);
      setFromEmail(data.from_email || "");
      setFromName(data.from_name || "");
      setAdmins((data.admin_emails || []).join(", "));
      setEnabled(data.enabled || enabled);
    });

  useEffect(() => {
    load();
     
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        from_email: fromEmail.trim(),
        from_name: fromName.trim(),
        admin_emails: admins,
        enabled,
      };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const { data } = await api.put("/superadmin/comms/settings", payload);
      setCfg(data);
      setApiKey("");
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="h-64 bg-white border border-stone animate-pulse rounded-sm" />;

  return (
    <form onSubmit={save} className="bg-white border border-stone rounded-sm p-6 space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6 text-copper" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-ink">Configuration Resend</h2>
      </div>

      <div className="bg-sand rounded-sm p-4 text-sm text-graphite flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-copper shrink-0 mt-0.5" />
        <div>
          <p>
            Provider actif : <strong className="text-ink">Resend</strong>. La clé API actuelle est{" "}
            {cfg.api_key_set ? (
              <strong className="text-ink">{cfg.api_key_masked}</strong>
            ) : (
              <strong className="text-ink">non configurée</strong>
            )}
            . Laissez le champ vide pour la conserver.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <Lbl>Clé API Resend</Lbl>
          <Input
            data-testid="comms-api-key"
            type="password"
            placeholder={cfg.api_key_set ? "•••••• (laisser vide pour conserver)" : "re_..."}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="mt-2"
          />
        </div>
        <div>
          <Lbl>Adresse expéditeur (FROM)</Lbl>
          <Input
            data-testid="comms-from-email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            className="mt-2"
            placeholder="noreply@clickexpert.fr"
          />
        </div>
        <div>
          <Lbl>Nom expéditeur</Lbl>
          <Input
            data-testid="comms-from-name"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            className="mt-2"
            placeholder="Déstockage Luminaires"
          />
        </div>
        <div className="md:col-span-2">
          <Lbl>Emails admin (notifications nouvelle commande)</Lbl>
          <Input
            data-testid="comms-admin-emails"
            value={admins}
            onChange={(e) => setAdmins(e.target.value)}
            className="mt-2"
            placeholder="admin@clickexpert.fr, autre@clickexpert.fr"
          />
          <p className="text-[11px] text-ash mt-1">Séparés par virgule, point-virgule ou retour à la ligne.</p>
        </div>
      </div>

      <div className="space-y-2 pt-2 border-t border-stone">
        <h3 className="text-xs uppercase tracking-[0.15em] text-ash mb-3">Notifications actives</h3>
        {[
          { k: "order_confirmation", lbl: "Confirmation de commande (client)" },
          { k: "new_order_admin", lbl: "Notification nouvelle commande (admin)" },
          { k: "password_reset", lbl: "Réinitialisation mot de passe" },
        ].map((it) => (
          <label key={it.k} className="flex items-center gap-3 text-sm text-graphite cursor-pointer">
            <input
              type="checkbox"
              data-testid={`comms-toggle-${it.k}`}
              checked={!!enabled[it.k]}
              onChange={(e) => setEnabled({ ...enabled, [it.k]: e.target.checked })}
              className="h-4 w-4 accent-copper"
            />
            {it.lbl}
          </label>
        ))}
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="comms-save-settings"
        className="bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] flex items-center gap-2 disabled:opacity-50"
      >
        <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </form>
  );
}

/* ---------------- Templates tab ---------------- */
function TemplatesTab() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [view, setView] = useState("preview"); // 'preview' | 'html'
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/superadmin/comms/templates");
    setList(data.templates);
    if (!active && data.templates.length) {
      const first = data.templates[0];
      setActive(first.type);
      setSubject(first.subject);
      setHtml(first.html);
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const current = useMemo(() => list.find((t) => t.type === active), [list, active]);

  const select = (ttype) => {
    const t = list.find((x) => x.type === ttype);
    if (!t) return;
    setActive(ttype);
    setSubject(t.subject);
    setHtml(t.html);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/comms/templates/${active}`, { subject, html });
      toast.success("Template enregistré");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Réinitialiser ce template aux valeurs par défaut ?")) return;
    try {
      const { data } = await api.post(`/superadmin/comms/templates/${active}/reset`);
      setSubject(data.subject);
      setHtml(data.html);
      toast.success("Template réinitialisé");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!list.length) return <div className="h-64 bg-white border border-stone animate-pulse rounded-sm" />;

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-6">
      <aside className="bg-white border border-stone rounded-sm p-2">
        {list.map((t) => (
          <button
            key={t.type}
            onClick={() => select(t.type)}
            data-testid={`comms-template-${t.type}`}
            className={`block w-full text-left px-3 py-3 text-sm rounded-sm transition-colors ${
              active === t.type ? "bg-sand text-ink" : "text-graphite hover:bg-sand/60"
            }`}
          >
            <div className="font-medium">{t.name}</div>
            <div className="text-[11px] text-ash mt-0.5">{t.type}</div>
          </button>
        ))}
      </aside>

      <div className="bg-white border border-stone rounded-sm p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] text-ash">{current?.type}</p>
            <h2 className="font-serif text-2xl text-ink">{current?.name}</h2>
            <p className="text-sm text-graphite mt-1">{current?.description}</p>
          </div>
          <button
            onClick={reset}
            data-testid="comms-template-reset"
            className="text-xs uppercase tracking-[0.15em] text-ash hover:text-ink flex items-center gap-1"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
          </button>
        </div>

        <div>
          <Lbl>Sujet</Lbl>
          <Input
            data-testid="comms-template-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-2"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Lbl>Corps HTML</Lbl>
            <div className="flex border border-stone rounded-sm overflow-hidden">
              <button
                onClick={() => setView("preview")}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1 ${
                  view === "preview" ? "bg-ink text-cream" : "text-graphite"
                }`}
              >
                <Eye className="h-3 w-3" /> Aperçu
              </button>
              <button
                onClick={() => setView("html")}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1 ${
                  view === "html" ? "bg-ink text-cream" : "text-graphite"
                }`}
              >
                <Code className="h-3 w-3" /> HTML
              </button>
            </div>
          </div>

          {view === "html" ? (
            <textarea
              data-testid="comms-template-html"
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              className="w-full h-[420px] border border-stone rounded-sm p-3 font-mono text-xs bg-sand/40"
              spellCheck={false}
            />
          ) : (
            <iframe
              title="preview"
              srcDoc={html}
              sandbox=""
              className="w-full h-[420px] border border-stone rounded-sm bg-white"
            />
          )}
        </div>

        {current?.variables?.length > 0 && (
          <div className="bg-sand/60 border border-stone rounded-sm p-3">
            <p className="text-[11px] uppercase tracking-[0.15em] text-ash mb-2">Variables disponibles</p>
            <div className="flex flex-wrap gap-2">
              {current.variables.map((v) => (
                <code
                  key={v}
                  className="text-[11px] px-2 py-1 bg-white border border-stone rounded-sm text-graphite"
                >
                  {`{{${v}}}`}
                </code>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={save}
          disabled={saving}
          data-testid="comms-template-save"
          className="bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] flex items-center gap-2 disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer le template"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Test tab ---------------- */
function TestTab() {
  const [to, setTo] = useState("");
  const [type, setType] = useState("test");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!to.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/superadmin/comms/test", {
        to: to.trim(),
        template_type: type,
        use_template: true,
      });
      toast.success(`Email envoyé. ID: ${data.provider_id || "—"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={send} className="bg-white border border-stone rounded-sm p-6 space-y-5 max-w-xl">
      <div className="flex items-center gap-3">
        <Send className="h-6 w-6 text-copper" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-ink">Envoyer un email de test</h2>
      </div>
      <p className="text-sm text-graphite">
        Vérifiez la configuration en envoyant un email à une adresse vérifiée sur votre compte Resend.
      </p>
      <div>
        <Lbl>Destinataire</Lbl>
        <Input
          data-testid="comms-test-to"
          type="email"
          required
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="mt-2"
          placeholder="vous@clickexpert.fr"
        />
      </div>
      <div>
        <Lbl>Template</Lbl>
        <select
          data-testid="comms-test-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="mt-2 w-full border border-stone rounded-sm p-2.5 text-sm bg-white"
        >
          <option value="test">Test (simple)</option>
          <option value="order_confirmation">Confirmation commande</option>
          <option value="new_order_admin">Notification admin</option>
          <option value="password_reset">Reset mot de passe</option>
        </select>
        <p className="text-[11px] text-ash mt-1">
          Les variables non fournies seront vides — normal pour un test.
        </p>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="comms-test-send"
        className="bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] flex items-center gap-2 disabled:opacity-50"
      >
        <Send className="h-4 w-4" /> {sending ? "Envoi…" : "Envoyer le test"}
      </button>
    </form>
  );
}

/* ---------------- Logs tab ---------------- */
function LogsTab() {
  const [logs, setLogs] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  const load = async () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.type = filterType;
    const { data } = await api.get("/superadmin/comms/logs", { params });
    setLogs(data.logs);
  };

  useEffect(() => {
    load();
     
  }, [filterStatus, filterType]);

  return (
    <div className="bg-white border border-stone rounded-sm p-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <h2 className="font-serif text-2xl text-ink">Journal des envois</h2>
        <div className="flex flex-wrap gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-stone rounded-sm p-2 text-xs bg-white"
          >
            <option value="">Tous les types</option>
            <option value="order_confirmation">Confirmation client</option>
            <option value="new_order_admin">Notif admin</option>
            <option value="password_reset">Reset mdp</option>
            <option value="test">Test</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-stone rounded-sm p-2 text-xs bg-white"
          >
            <option value="">Tous les statuts</option>
            <option value="sent">Envoyé</option>
            <option value="error">Erreur</option>
          </select>
          <button onClick={load} className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-stone hover:bg-sand">
            Rafraîchir
          </button>
        </div>
      </div>

      {!logs ? (
        <div className="h-40 bg-sand/40 animate-pulse rounded-sm" />
      ) : logs.length === 0 ? (
        <p className="text-graphite text-sm py-8 text-center">Aucun envoi pour le moment.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-stone text-[11px] uppercase tracking-[0.15em] text-ash">
              <tr>
                <th className="text-left py-3">Date</th>
                <th className="text-left py-3">Type</th>
                <th className="text-left py-3">Destinataire</th>
                <th className="text-left py-3">Sujet</th>
                <th className="text-left py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-sand">
                  <td className="py-3 text-graphite whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString("fr-FR")}
                  </td>
                  <td className="py-3 text-graphite">{TYPE_LABEL[l.type] || l.type}</td>
                  <td className="py-3 text-ink">{l.recipient}</td>
                  <td className="py-3 text-graphite max-w-[300px] truncate" title={l.subject}>
                    {l.subject}
                  </td>
                  <td className="py-3">
                    {l.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                        <CheckCircle2 className="h-4 w-4" /> Envoyé
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-red-700 text-xs"
                        title={l.error || ""}
                      >
                        <XCircle className="h-4 w-4" /> Erreur
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function SuperCommunication() {
  const { user } = useAuth();
  const [tab, setTab] = useState("settings");

  if (user?.role !== "superadmin")
    return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  const tabs = [
    { id: "settings", label: "Paramètres" },
    { id: "templates", label: "Templates" },
    { id: "test", label: "Test d'envoi" },
    { id: "logs", label: "Journal" },
  ];

  return (
    <div data-testid="super-communication">
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Super Admin</p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-2">Communication</h1>
      <p className="text-graphite mb-8 max-w-2xl">
        Gérez vos notifications transactionnelles : configuration du provider (Resend), templates HTML, tests et journal des envois.
      </p>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === "settings" && <SettingsTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "test" && <TestTab />}
      {tab === "logs" && <LogsTab />}
    </div>
  );
}
